import classNames from 'classnames'
import { t } from 'i18next'
import { useContext, useEffect, useRef, useState } from 'react'
import 'react-datepicker/dist/react-datepicker.css'
import { PulseLoader } from 'react-spinners'
import childPlanFormDataContext from '../../../contexts/ChildPlanFormDataContext'
import ParentFormDataContext from '../../../contexts/ParentFormDataContext'
import returnCustomerFormDataContext from '../../../contexts/ReturnCustomerFormDataContext'
import { IFormDataInput } from '../../../model/IFormDataInput'
import { IPlan } from '../../../model/IPlan'
import { filterPlans, setChildSectionTitle, setPlanOptionMainText } from '../../../utils/childPlanHelper'
import ButtonSelect, {
    IButtonSelectItem,
} from '../../buttonSelect/ButtonSelect'
import DropDownList, { ddlListItem } from '../../dropDownList/DropDownList'
import InputBox from '../../inputBox/InputBox'

import config from '../../../config'
import { ResponsiveWidth } from '../../../contexts/contextDataObjects/ParentDataObject'
import { formatCAD } from '../../../utils/currencyFormatHelper'
import { fetchPolicyQuote } from '../../../utils/fetchPolicyQuote'
import { isDropDownListItemsEmpty } from '../../../utils/formDataInputHelper'
import { formatInputDate, parseDate } from '../../../utils/patternHelper'
import CalendarIcon from '../../datetimePicker/CalendarIcon'

export interface IChildInfo {
    className?: string
    childIndex: number
    plans: IPlan[]
    premiumOptions: { [key: string]: number }
    plansLoading: boolean
    premiumsLoading: boolean
}

const ChildInfo: React.FC<IChildInfo> = ({
    className,
    childIndex,
    plans,
    premiumOptions,
    plansLoading,
    premiumsLoading,
}) => {
    const [isLoading, setIsLoading] = useState<boolean>(false) //used for making a component responsive with a spinner (for the future)

    const { parentFormData, setParentFormData } = useContext(
        ParentFormDataContext
    )
    const { childPlanFormData, setChildPlanFormData } = useContext(
        childPlanFormDataContext
    )

    const { returnCustomerFormData, setReturnCustomerFormData } = useContext(
        returnCustomerFormDataContext
    )

    const childPlan = childPlanFormData[childIndex]

    const [planOptions, setPlanOptions] = useState<IButtonSelectItem[]>([])
    const childPlanFormRef = useRef<HTMLDivElement>(null)
    const [inputDateValue, setInputDateValue] = useState<string>('');
    const maxAge = Number(config.REACT_APP_MAXAGE);
    const minAge = Number(config.REACT_APP_MINAGE)
    const minDate = new Date()
    minDate.setMonth(minDate.getMonth() - minAge)
    const maxDate = new Date()
    maxDate.setFullYear(maxDate.getFullYear() - maxAge);

    function updateDDLItems(listName: string, options: ddlListItem[]) {
        const dataInput = childPlan.ChildPlanFormDataInput.find(
            (x) => x.Name === listName
        )

        if (dataInput != null) {
            if (parentFormData.OrgGuid && listName === 'School' && parentFormData.CommonChildPlanFormInputData.Disabled == true) {
                const school = options.find(x => x.name === 'N/A') || { name: '', id: '' }
                const updated = {
                    ...dataInput,
                    ListItems: options,
                    IsBlur: true,
                    IsFocus: false,
                    IsValid: true,
                    Disabled: true,
                }
                const updatedFormInputs = [...childPlan.ChildPlanFormDataInput]
                const index = updatedFormInputs.indexOf(dataInput)
                updatedFormInputs[index] = updated

                setChildPlanFormData((prevFormData) => {
                    const newFormData = [...prevFormData]
                    newFormData[childIndex] = {
                        ...childPlan,
                        School: school,
                        ChildPlanFormDataInput: updatedFormInputs,
                    }
                    return newFormData
                })
            } else {
                const updated = {
                    ...dataInput,
                    ListItems: options,
                }
                const updatedFormInputs = [...childPlan.ChildPlanFormDataInput]
                const index = updatedFormInputs.indexOf(dataInput)
                updatedFormInputs[index] = updated

                setChildPlanFormData((prevFormData) => {
                    const newFormData = [...prevFormData]
                    newFormData[childIndex] = {
                        ...childPlan,
                        ChildPlanFormDataInput: updatedFormInputs,
                    }
                    return newFormData
                })
            }
        }
    }

    useEffect(() => {
        if (parentFormData.SchoolListItems.length > 0) {
            updateDDLItems('School', parentFormData.SchoolListItems)
        }
    }, [parentFormData.SchoolListItems])

    useEffect(() => {
        updatePlanOptions()
    }, [plans, premiumOptions, parentFormData?.PlanDuration])

    useEffect(() => {
        if (planOptions.length === 1) {
            if (parentFormData.SchoolListItems.length > 0) {
                updateDDLItems('School', parentFormData.SchoolListItems)
            }
            subscribePlanOptionsQRCode()
        }
        else if (planOptions.length > 0) {
            subscribePlanOptions()
        }
    }, [planOptions])

    useEffect(() => {
        if (planOptions.length > 0 && childPlan.Plan.Primary === '' && childPlan.Plan.Premium !== undefined) {
            subscribePlanOptions()
        }

        if (isDropDownListItemsEmpty('School', childPlan.ChildPlanFormDataInput)
            && (childPlan.School.name !== '')) {
            updateDDLItems('School', parentFormData.SchoolListItems)
        }

        if (isDropDownListItemsEmpty('School', childPlan.ChildPlanFormDataInput)
            && (parentFormData.SchoolBoard.name !== '')) {
            updateDDLItems('School', parentFormData.SchoolListItems)
        }
    }, [childPlan])

    useEffect(() => {
        if (childPlan.SetValidationFocus) {
            childPlanFormRef.current?.scrollIntoView({ behavior: 'smooth' })

            setChildPlanFormData((prevFormData) => {
                const newFormData = [...prevFormData]
                newFormData[childIndex] = {
                    ...childPlan,
                    ['SetValidationFocus']: false,
                }
                return newFormData
            })
        }
    }, [childPlan.SetValidationFocus])

    const subscribePlanOptions = () => {
        setChildPlanFormData((prevFormData) => {
            const newFormData = [...prevFormData]

            newFormData[childIndex] = {
                ...newFormData[childIndex],
                ['Plan']: {
                    PlanCode: childPlan.Plan.PlanCode,
                    Primary:
                        planOptions.find(
                            (x) => x.value === childPlan.Plan.PlanCode
                        )?.mainText || '',
                    Premium: parentFormData.FamilyRate ? childPlan.Plan.Premium : premiumOptions[childPlan.Plan.PlanCode],
                    Index:
                        planOptions.find(
                            (x) => x.value === childPlan.Plan.PlanCode
                        )?.index || 0,
                    IsValid: false
                }
            }
            return newFormData
        })
    }

    const subscribePlanOptionsQRCode = () => {
        setChildPlanFormData((prevFormData) => {
            const newFormData = [...prevFormData]
            newFormData[childIndex] = {
                ...newFormData[childIndex],
                ['Plan']: {
                    PlanCode: planOptions[0].value,
                    Primary: planOptions[0].mainText,
                    Premium: premiumOptions[planOptions[0].value],
                    Index: planOptions[0].index,
                    IsValid: true
                }
            }
            fetchPolicyQuote(
                parentFormData,
                newFormData,
                setParentFormData,
                setChildPlanFormData
            )
            return newFormData
        })
    }

    const updatePlanOptions = () => {
        const mappedPlans = plans
            .slice(0)
            .reverse()
            .filter(plan => filterPlans(parentFormData.OrgGuid, plan.plancode))
            .map((plan, index) => ({
                mainText: setPlanOptionMainText(plan.plandesc, parentFormData.Language),
                secondaryText:
                    Object.keys(premiumOptions).length > 0
                        ? formatCAD(
                            Number(premiumOptions[
                                plan.plancode.slice(0, -1) +
                                parentFormData.PlanDuration.Duration
                            ]?.toFixed(2)), parentFormData.Language)
                        : '',
                name: 'Plan',
                index: index,
                value:
                    plan.plancode.slice(0, -1) +
                    parentFormData.PlanDuration.Duration.toString(),
            }))
        setPlanOptions(mappedPlans)
    }

    const handleButtonSelectChange = (
        e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
        primary: string,
        secondary: string,
        i: number
    ) => {
        const { name, value } = e.currentTarget
        switch (name) {
            case 'Plan':
                setChildPlanFormData((prevFormData) => {
                    const newFormData = [...prevFormData]
                    newFormData[childIndex] = {
                        ...childPlan,
                        [name]: {
                            PlanCode: value,
                            Primary: primary,
                            Premium: premiumOptions[value],
                            Index: i,
                            IsValid: true
                        },
                    }
                    fetchPolicyQuote(
                        parentFormData,
                        newFormData,
                        setParentFormData,
                        setChildPlanFormData
                    )
                    return newFormData
                })
                break
        }
    }

    const handleDDLChange = (name: string, value: any) => {
        let isValid: boolean = false
        let errorMessage: string = ''

        const dataInput = childPlan.ChildPlanFormDataInput.find(
            (x) => x.Name === name
        )

        if (dataInput != null) {
            // Validation - required
            if (dataInput.Required && !value?.name) {
                isValid = false

                const temp = dataInput.ErrorMessageList.find(
                    (x) => x.type === 'required'
                )

                if (temp != null) {
                    errorMessage = temp.error
                }
            } else {
                isValid = true
            }

            // Validation - pattern match
            if (isValid) {
                const regex = RegExp(dataInput.Pattern)

                if (dataInput.Pattern !== '' && !regex.test(value)) {
                    isValid = false
                    const temp = dataInput.ErrorMessageList.find(
                        (x) => x.type === 'pattern'
                    )

                    if (temp != null) {
                        errorMessage = temp.error
                    }
                } else {
                    isValid = true
                }
            }

            const updated = {
                ...dataInput,
                IsBlur: true,
                IsFocus: false,
                IsValid: isValid,
                ErrorMessage: errorMessage,
            }
            const updatedFormInputs = [...childPlan.ChildPlanFormDataInput]
            const index = updatedFormInputs.indexOf(dataInput)
            updatedFormInputs[index] = updated

            setChildPlanFormData((prevFormData) => {
                const newFormData = [...prevFormData]
                newFormData[childIndex] = {
                    ...childPlan,
                    [name]: value,
                    ChildPlanFormDataInput: updatedFormInputs,
                }
                return newFormData
            })
        }
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target
        let isValid: boolean = false
        let errorMessage: string = ''
        const dataInput = childPlan.ChildPlanFormDataInput.find(
            (x) => x.Name === name
        )
        if (dataInput != null) {
            // Validation - required
            if (dataInput.Required && !value) {
                isValid = false

                const temp = dataInput.ErrorMessageList.find(
                    (x) => x.type === 'required'
                )

                if (temp != null) {
                    errorMessage = temp.error
                }
            } else {
                isValid = true
            }

            // Validation - pattern match
            if (isValid) {
                const regex = RegExp(dataInput.Pattern)

                if (dataInput.Pattern !== '' && !regex.test(value)) {
                    isValid = false
                    const temp = dataInput.ErrorMessageList.find(
                        (x) => x.type === 'pattern'
                    )

                    if (temp != null) {
                        errorMessage = temp.error
                    }
                } else {
                    isValid = true
                }
            }
            const updated = {
                ...dataInput,
                IsBlur: true,
                IsFocus: false,
                IsValid: isValid,
                ErrorMessage: errorMessage,
            }
            const updatedFormInputs = [...childPlan.ChildPlanFormDataInput]
            const index = updatedFormInputs.indexOf(dataInput)
            updatedFormInputs[index] = updated
            setChildPlanFormData((prevFormData) => {
                const newFormData = [...prevFormData]
                newFormData[childIndex] = { ...childPlan, [name]: value, ChildPlanFormDataInput: updatedFormInputs }
                return newFormData
            })
        }
    }

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const { name, value } = e.target
        let isValid: boolean = false
        let errorMessage: string = ''

        const dataInput = childPlan.ChildPlanFormDataInput.find(
            (x) => x.Name === name
        )

        if (dataInput != null) {
            // Validation - required
            if (dataInput.Required && !value) {
                isValid = false

                const temp = dataInput.ErrorMessageList.find(
                    (x) => x.type === 'required'
                )

                if (temp != null) {
                    errorMessage = temp.error
                }
            } else {
                isValid = true
            }

            // Validation - pattern match
            if (isValid) {
                const regex = RegExp(dataInput.Pattern)

                if (dataInput.Pattern !== '' && !regex.test(value)) {
                    isValid = false
                    const temp = dataInput.ErrorMessageList.find(
                        (x) => x.type === 'pattern'
                    )

                    if (temp != null) {
                        errorMessage = temp.error
                    }
                } else {
                    isValid = true
                }
            }

            const updated = {
                ...dataInput,
                IsBlur: true,
                IsFocus: false,
                IsValid: isValid,
                ErrorMessage: errorMessage,
            }
            const updatedFormInputs = [...childPlan.ChildPlanFormDataInput]
            const index = updatedFormInputs.indexOf(dataInput)
            updatedFormInputs[index] = updated

            setChildPlanFormData((prevFormData) => {
                const newFormData = [...prevFormData]
                newFormData[childIndex] = {
                    ...childPlan,
                    ChildPlanFormDataInput: updatedFormInputs,
                }
                return newFormData
            })
        }
    }

    const getPlanDetailUrl = (provinceId: string) => {
        return provinceId === 'MB' ? t('url_mb') : t('url_others')
    }

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        const { name } = e.target
        const dataInput = childPlan.ChildPlanFormDataInput.find(
            (x) => x.Name === name
        )

        if (dataInput != null) {
            const updated = {
                ...dataInput,
                IsBlur: false,
                IsFocus: true,
            }
            const updatedFormInputs = [...childPlan.ChildPlanFormDataInput]
            const index = updatedFormInputs.indexOf(dataInput)
            updatedFormInputs[index] = updated

            setChildPlanFormData((prevFormData) => {
                const newFormData = [...prevFormData]
                newFormData[childIndex] = {
                    ...childPlan,
                    ChildPlanFormDataInput: updatedFormInputs,
                }
                return newFormData
            })
        }
    }

    const handleDatePickerSelect = (selectedDate: Date) => {
        const updatedFormInputs = childPlan.ChildPlanFormDataInput.map(
            (dataInput) => {
                if (dataInput.Name === 'Dob') {
                    return {
                        ...dataInput,
                        IsValid: true,
                    }
                }
                return dataInput
            }
        )
        setChildPlanFormData((prevFormData) => {
            const newFormData = [...prevFormData]
            newFormData[childIndex] = {
                ...childPlan,
                ChildPlanFormDataInput: updatedFormInputs,
                Dob: selectedDate,
            }
            return newFormData
        })
        setInputDateValue(parseDate(selectedDate))
    }

    const handleDateChange = (date: Date | null) => {
        setChildPlanFormData((prevFormData) => {
            let name = 'Dob'
            const newFormData = [...prevFormData]
            newFormData[childIndex] = {
                ...childPlan,
                [name]: date,
            }
            return newFormData
        })
        setInputDateValue(parseDate(date))
    }

    const handleInputDateChange = (event: any) => {
        const newValue = formatInputDate(event.target.value);
        if (newValue === '') {
            setChildPlanFormData((prevFormData) => {
                let name = 'Dob'
                const newFormData = [...prevFormData]
                newFormData[childIndex] = {
                    ...childPlan,
                    [name]: newValue,
                }
                return newFormData
            })
        }
        setInputDateValue(newValue);
    };
    const handleInputDateBlur = (e: any) => {
        const { name, value } = e.target
        let isValid: boolean = false
        let errorMessage: string = ''

        const dataInput = childPlan.ChildPlanFormDataInput.find(
            (x) => x.Name === name
        )
        const inputDate = new Date(e.target.value);
        let date: any = null;

        if (dataInput != null) {
            // Validation - required
            if (dataInput.Required && !value) {
                isValid = false
                errorMessage = 'required'
            } else {
                isValid = true
            }
            // Validation - pattern match
            if (isValid) {
                const regex = RegExp(dataInput.Pattern)
                if ((dataInput.Pattern !== '' && !regex.test(value)) || !(inputDate < minDate && inputDate > maxDate)) {
                    isValid = false
                    errorMessage = 'required'
                    setInputDateValue('')

                } else {
                    isValid = true
                    date = new Date(value);
                }
            }
            const updated = {
                ...dataInput,
                IsBlur: true,
                IsFocus: false,
                IsValid: isValid,
                ErrorMessage: errorMessage,
            }
            const updatedFormInputs = [...childPlan.ChildPlanFormDataInput]
            const index = updatedFormInputs.indexOf(dataInput)
            updatedFormInputs[index] = updated
            setChildPlanFormData((prevFormData) => {
                let name = 'Dob'
                const newFormData = [...prevFormData]
                newFormData[childIndex] = {
                    ...childPlan,
                    ChildPlanFormDataInput: updatedFormInputs,
                    [name]: date,
                }
                return newFormData
            })
        }
    };
    return (
        <>
            <h1 className="text-lg font-bold text-center pb-3">
                {t(setChildSectionTitle(childPlan, parentFormData))}
            </h1>
            <div
                ref={childPlanFormRef}
                className={classNames(className, 'h-auto flex items-center', 'lg:grid lg:grid-cols-2 lg:space-x-2 flex-col',
                    returnCustomerFormData.IsLoading ? 'blur-sm' : ''
                )}
            >
                {childPlan.ChildPlanFormDataInput.map(
                    (input: IFormDataInput) => {
                        if (input.ControlType === 'InputBox') {
                            return (
                                <InputBox
                                    key={input.Id}
                                    type={input.Type}
                                    inputID={input.Id + childIndex}
                                    label={t(input.Label) || ''}
                                    width={input.Width}
                                    height={input.Height}
                                    className={input.ClassName}
                                    shadow={input.Shadow}
                                    onChange={(
                                        e: React.ChangeEvent<HTMLInputElement>
                                    ) => handleChange(e)}
                                    onBlur={(
                                        e: React.FocusEvent<HTMLInputElement>
                                    ) => handleBlur(e)}
                                    onFocus={(
                                        e: React.FocusEvent<HTMLInputElement>
                                    ) => handleFocus(e)}
                                    value={childPlan[input.Name]}
                                    name={input.Name}
                                    required={input.Required}
                                    error={t(input.ErrorMessage) || ''}
                                    isValid={input.IsValid}
                                    isBlur={input.IsBlur}
                                    isFocus={input.IsFocus}
                                />
                            )
                        } else if (input.ControlType === 'DatePicker') {
                            return (
                                <div key={input.Id}>
                                    <div className="flex flex-row justify-start relative">
                                        <InputBox
                                            key={input.Id}
                                            type={input.Type}
                                            inputID={input.Id + childIndex}
                                            label={t(input.Label) || ''}
                                            width={input.Width}
                                            height={input.Height}
                                            className={input.ClassName}
                                            shadow={input.Shadow}
                                            onChange={(
                                                e: React.ChangeEvent<HTMLInputElement>
                                            ) => handleInputDateChange(e)}
                                            onBlur={(
                                                e: any
                                            ) => handleInputDateBlur(e)}
                                            onFocus={(
                                                e: any
                                            ) => handleFocus(e)}
                                            value={inputDateValue ? inputDateValue : (childPlan.Dob ? parseDate(childPlan.Dob) : '')}
                                            name={input.Name}
                                            required={input.Required}
                                            error={t(input.ErrorMessage) || ''}
                                            isValid={input.IsValid}
                                            isBlur={input.IsBlur}
                                            isFocus={input.IsFocus}
                                            placeholder={t(input.Placeholder) || undefined}
                                        />
                                        <div className='absolute inset-y-0 right-0 flex items-center pr-3 justify-center pt-2'>
                                            <CalendarIcon
                                                key={input.Id}
                                                name={input.Name}
                                                inputID={input.Id + input.Name + childIndex}
                                                maxDate={minDate}
                                                minDate={maxDate}
                                                maxYear={maxAge}
                                                handleDateChange={handleDateChange}
                                                selectedDate={childPlan.Dob}
                                                onBlur={handleBlur}
                                                onFocus={handleFocus}
                                                onSelect={handleDatePickerSelect}
                                            />
                                        </div>
                                    </div>
                                    <p className={classNames("-mt-1 text-gray-600 text-xs text-left ml-1.5 lg:pb-0 pb-2", ResponsiveWidth)}>
                                        {t('21yearsOlderText')}
                                    </p>
                                </div>
                            )
                        } else if (input.ControlType === 'DropDownList') {
                            return (
                                <DropDownList
                                    key={input.Id}
                                    className={input.ClassName}
                                    width={input.Width}
                                    height={input.Height}
                                    list={input.ListItems || []}
                                    label={t(input.Label) || ''}
                                    inputID={input.Id + childIndex}
                                    selectedItem={childPlan[input.Name]}
                                    name={input.Name}
                                    required={input.Required}
                                    onChange={handleDDLChange}
                                    onBlur={handleBlur}
                                    onFocus={handleFocus}
                                    error={t(input.ErrorMessage) || ''}
                                    isValid={input.IsValid}
                                    isBlur={input.IsBlur}
                                    isFocus={input.IsFocus}
                                    disabled={input.Disabled}
                                />
                            )
                        }
                    }
                )}
            </div>
            <div
                className={classNames(
                    'h-auto flex flex-col items-center justify-center z-10',
                    returnCustomerFormData.IsLoading ? 'blur-sm' : ''
                )}
            >
                <h1 className="text-lg font-bold text-center pt-4">
                    {t('planSelection')}
                </h1>
                <p className={classNames("pt-4 font-medium md:text-lg text-base", "lg:-ml-[17rem] md:-ml-[14rem] -ml-[10rem]")}>
                    {t('choosePlan')}
                </p>
                {plansLoading ? (
                    <>
                        <PulseLoader color="white" size={25} />
                    </>
                ) : (
                    <>
                        <ButtonSelect
                            options={planOptions}
                            className='z-5 hover:bg-[#9ccb45]'
                            selectColor="bg-[#9ccb45]"
                            baseColor="bg-white"
                            textColor="black"
                            size="md:h-[4rem] h-[3.5rem] lg:w-[8rem] md:w-[7rem] w-[5.5rem]"
                            textSize="md:text-lg text-sm"
                            secondaryTextSize="md:text-lg text-sm"
                            onChange={handleButtonSelectChange}
                            selected={
                                childPlanFormData[childIndex].Plan.PlanCode
                            }
                            loadingSecondary={premiumsLoading}
                        />
                        <div>
                            {childPlanFormData[childIndex].Plan.IsValid && !childPlanFormData[childIndex].Plan.PlanCode &&
                                (
                                    <p className="text-red-600 font-semibold mt-2">
                                        *{t('required')}
                                    </p>
                                )}
                        </div>
                        <a
                            href={getPlanDetailUrl(
                                parentFormData?.Province?.id || ''
                            )}
                            target="_blank"
                            className="pt-4 text-lg text-sky-700 text-center"
                        >
                            <u>{t('viewPlanDetailsText')}</u>
                        </a>
                    </>
                )}
            </div>
        </>
    )
}

export default ChildInfo
